import * as React from 'react';
import { RootState } from 'redux/rootReducer';
import { ThunkDispatch } from 'redux-thunk';
import * as labelsDispatch from 'redux/ducks/data/labels';
import { LabelActions } from 'redux/ducks/data/labels';
import { connect } from 'react-redux';
import { useEffect } from 'react';
import { Label } from 'redux/ducks/data/registrationPoints';
import { useIntl } from 'react-intl';
import SelectItem from 'pages/Report/components/Select/components/SelectItem';
import { Button, Divider, Grid, ListItemText } from '@material-ui/core';
import ApplyButton from 'pages/Report/components/ApplyButton/ApplyButton';
import Select from 'pages/Report/components/Select';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface LabelsSelectProps {
  selected: string[];
  onChange: (selected: string[]) => void;
}

interface SelectionDraft {
  selection: string[];
  changed?: boolean;
}

const SelectAll = 'all';

const LabelsSelect: React.FunctionComponent<LabelsSelectProps & StateProps & DispatchProps> = (
  props
) => {
  const intl = useIntl();
  const { getLabels, labels = [], selected, onChange } = props;

  const [draft, setDraft] = React.useState<SelectionDraft>({ selection: selected, changed: false });
  const [pending, setPending] = React.useState(false);

  useEffect(() => {
    void getLabels();
  }, []);

  React.useEffect(() => {
    setDraft({ selection: selected, changed: false });
    setPending(false);
  }, [selected]);

  const formattedLabelName = (label: Label): string =>
    label.type === 'system' ? intl.formatMessage({ id: `label.system.${label.name}` }) : label.name;

  const getLabelById = (labelId: string) => labels.find((label) => label.id === labelId);

  const renderValue = React.useCallback(() => {
    if (selected.length === 0) {
      return intl.formatMessage({ id: 'report.filter.no_selection' });
    }

    return selected
      .map((id) => {
        const label = getLabelById(id);
        return label ? formattedLabelName(label) : id;
      })
      .join(',');
  }, [selected]);

  const handleDraftChange = (selection: string[]) => {
    const changed =
      selection.some((id) => !selected.includes(id)) ||
      selected.some((id) => !selection.includes(id));
    setDraft({ selection, changed });
  };

  const handleChange = (e: React.ChangeEvent<any>, value: string, isChecked?: boolean) => {
    if (value === SelectAll) {
      handleDraftChange([]);
    } else {
      const selectedValues = isChecked
        ? draft.selection.filter((selectedId) => selectedId !== value)
        : [...draft.selection, value];
      handleDraftChange(selectedValues);
    }
  };

  const handleResetChanges = () => {
    setDraft({ selection: selected, changed: false });
  };

  const handleApplyChanges = () => {
    if (pending || !draft.changed) {
      return;
    }
    setPending(true);
    onChange(draft.selection);
  };

  return (
    <Select
      fullWidth
      renderValue={renderValue}
      buttonProps={{
        fullWidth: true,
        disabled: labels.length === 0,
        startIcon: <LocalOfferIcon />
      }}
      menuProps={{
        MenuListProps: {
          style: { minWidth: '220px' },
          disablePadding: true
        }
      }}
      disabled={labels.length === 0}
    >
      <SelectItem
        disabled={draft.selection.length === 0}
        selected={draft.selection.length === 0}
        onClick={(e: React.ChangeEvent) => handleChange(e, SelectAll)}
        checkbox
      >
        <ListItemText>{intl.formatMessage({ id: 'report.filter.no_selection' })}</ListItemText>
      </SelectItem>
      <Divider />
      {labels.map((label) => {
        const isSelected = draft.selection.some((id) => id === label.id);
        return (
          <SelectItem
            onClick={(e: React.ChangeEvent) => handleChange(e, label.id, isSelected)}
            key={label.id}
            selected={isSelected}
            checkbox
          >
            <ListItemText>{formattedLabelName(label)}</ListItemText>
          </SelectItem>
        );
      })}
      {draft.changed && (
        <SelectItem
          disableLookup
          disableRipple
          disableHover
          style={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fff',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Grid container item justify='space-between'>
            <Button variant='text' color='primary' onClick={handleResetChanges}>
              {intl.formatMessage({ id: 'base.undo' })}
            </Button>
            <ApplyButton variant='outlined' color='primary' onClick={handleApplyChanges}>
              {intl.formatMessage({ id: 'base.apply' })}
            </ApplyButton>
          </Grid>
        </SelectItem>
      )}
    </Select>
  );
};

const mapStateToProps = (state: RootState) => ({
  labels: state.data.labels.labels
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, void, LabelActions>) => ({
  getLabels: () => dispatch(labelsDispatch.getLabels())
});

export default connect<StateProps, DispatchProps, LabelsSelectProps>(
  mapStateToProps,
  mapDispatchToProps
)(LabelsSelect);
